import { invoicesHere } from '../invoices'
import type { InvoiceRef } from '../bank-match'
import { api, dismiss, getQueue, mutate, type Ack } from '../api'
import { matchPayments, payerNameOf, withPayerName, type AlreadyPaidMatch, type Candidate, type MatchResult, type OwedVisit, type PaidVisit, type PaymentMatch, type PayerCustomer } from '../bank-match'
import { pdfPages, statementRowsFromPdf } from '../bank-pdf'
import { readStatement, type BankPayment } from '../bank-statement'
import { parseCsv, tidyRows } from '../import-sheet'
import { getMe } from '../me'
import { hasExtraJobs, loadRoundJobs, severalJobsOn, visitJobLabel } from '../job-names'
import { visitJobId } from '../jobs'
import type { OwedPayload } from '../owed-view'
import { routeGen } from '../route-state'
import type { Clean, Customer } from '../types'
import { clear, el, money, shortDate, todayISO } from '../ui'
import { accountsAsOwed, isAccount, splitPaid, switchFlag, type Flag, type SwitchInfo } from '../bank-brought'
import { toCollect } from '../brought'

// Tick off bank payments (#/bank, owners; Liam 2026-09-27, plan in
// docs/plans/bank-statement-matching.md). The owner chooses a statement
// download; the phone reads the money in, matches each payment to who owes
// (src/bank-match.ts) and shows three lists: sure, check, not matched.
// Nothing is marked paid until the owner taps, and the statement never
// leaves the phone: only "paid by bank" for each chosen visit is sent, with
// which payment paid it, so the same payment is never used twice.

const MAX_BYTES = 5 * 1024 * 1024

interface Loaded {
  // For a PDF: what Roundly read in, against the total the statement prints.
  pdfCheck: { statedIn: number | null; readIn: number } | null
  payments: BankPayment[]
  ignored: number
  result: MatchResult
  customers: Map<number, Customer>
  // Money brought from Squeegee (0043, release step 5): each customer's switch day with their Squeegee payments, and
  // the bank payments the owner already said are new money. Empty on a round without brought money.
  switches: SwitchInfo[]
  newMoney: Set<string>
}

// A statement saved by Excel on Windows is often not UTF-8.
async function readText(f: File): Promise<string> {
  const bytes = new Uint8Array(await f.arrayBuffer())
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes)
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes)
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return new TextDecoder('windows-1252').decode(bytes)
  }
}

async function rowsOf(f: File): Promise<{ rows: string[][]; pdfCheck: Loaded['pdfCheck']; problem?: string }> {
  if (/\.pdf$/i.test(f.name) || f.type === 'application/pdf') {
    const read = statementRowsFromPdf(await pdfPages(await f.arrayBuffer()))
    if (read.problem) return { rows: [], pdfCheck: null, problem: read.problem }
    return { rows: read.rows, pdfCheck: { statedIn: read.statedIn, readIn: read.readIn } }
  }
  if (/\.(csv|txt|tsv)$/i.test(f.name) || f.type === 'text/csv') return { rows: parseCsv(await readText(f)), pdfCheck: null }
  const XLSX = await import('xlsx')
  const book = XLSX.read(await f.arrayBuffer(), { type: 'array', cellDates: false })
  const sheet = book.Sheets[book.SheetNames[0]]
  // Every date cell, whatever its Excel format (short date, date and time),
  // is written out as yyyy-mm-dd, never month-first "9/5/26" (review rounds
  // 1 and 2, finding 3).
  for (const [addr, cell] of Object.entries(sheet)) {
    if (addr.startsWith('!') || !cell || typeof cell !== 'object') continue
    const c = cell as { t?: string; v?: unknown; z?: string; w?: string }
    if (c.t === 'n' && typeof c.v === 'number' && c.z && XLSX.SSF.is_date(c.z)) {
      const d = XLSX.SSF.parse_date_code(c.v)
      if (d) {
        c.w = `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`
        c.t = 's'
        c.v = c.w
      }
    }
  }
  return { rows: tidyRows(XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: '' })), pdfCheck: null }
}

const addDays = (iso: string, n: number): string => {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

export function mountBank(): void {
  const root = document.querySelector<HTMLElement>('#screen')!
  clear(root)
  if (getMe()?.role !== 'owner') {
    root.append(el('p', { class: 'empty-note' }, 'Only the owner of a round can tick off bank payments.'))
    return
  }
  root.append(
    el('div', { class: 'page-heading' }, el('div', {}, el('h2', {}, 'Tick off bank payments'), el('p', { class: 'muted' }, 'Roundly matches the money in on your bank statement to who owes you. You check before anything is marked paid.'))),
  )
  const box = el('div', { class: 'import-body' })
  root.append(box)
  showChoose(box)
}

function showChoose(box: HTMLElement, problem?: string): void {
  clear(box)
  const input = el('input', { type: 'file', accept: '.pdf,.csv,.txt,.xlsx,.xls,application/pdf,text/csv', class: 'import-file' }) as HTMLInputElement
  const choose = el('button', { type: 'button', class: 'btn-primary import-choose' }, 'Choose your bank statement')
  choose.addEventListener('click', () => input.click())
  input.addEventListener('change', () => {
    const f = input.files?.[0]
    if (f) void readFile(box, f)
  })
  const card = el('section', { class: 'import-card' })
  card.append(
    el('h3', {}, 'Get your statement'),
    el(
      'ul',
      { class: 'import-tips' },
      el('li', {}, 'In your online banking, download your statement or your recent transactions.'),
      el('li', {}, el('strong', {}, 'A PDF statement, CSV or Excel file '), 'all work. Any dates you like: the last week is fine.'),
      el('li', {}, 'Then choose that file here. It stays on your phone: only who paid is saved.'),
    ),
  )
  if (problem) card.append(el('p', { class: 'form-error', role: 'alert' }, problem))
  card.append(choose, input, el('p', {}, el('a', { href: '#/owed', class: 'link-button' }, 'Back to Owed')))
  box.append(card)
}

async function readFile(box: HTMLElement, f: File): Promise<void> {
  if (f.size > MAX_BYTES) {
    showChoose(box, 'That file is too big (over 5 MB). Download a shorter date range and try again.')
    return
  }
  const gen = routeGen
  clear(box)
  box.append(el('p', { class: 'muted', role: 'status' }, `Reading ${f.name}…`))
  let got: Awaited<ReturnType<typeof rowsOf>>
  try {
    got = await rowsOf(f)
  } catch {
    showChoose(box, "Roundly couldn't read that file. If it is a PDF with a password, download it again without one, or try the CSV download.")
    return
  }
  if (got.problem) {
    showChoose(box, got.problem)
    return
  }
  const read = readStatement(got.rows)
  if (read.problem) {
    showChoose(box, `${read.problem} Check it is the statement or transactions download from your bank.`)
    return
  }
  // The statement's dates, and the visits already marked paid in the 120
  // days before it (a payment usually comes after the clean), so those
  // payments read "Already paid" rather than "Not matched" (28 Sep 2026).
  const firstDay = read.payments.reduce((m, p) => (p.date < m ? p.date : m), read.payments[0].date)
  const lastDay = read.payments.reduce((m, p) => (p.date > m ? p.date : m), read.payments[0].date)
  const until = addDays(lastDay, 1)
  const reach = addDays(firstDay, -120)
  const floor = addDays(until, -540)
  const paidSince = reach < floor ? floor : reach
  let owed: OwedPayload
  let customers: Customer[]
  let refs: string[]
  let choices: { paid_ref: string }[] = []
  let switches: SwitchInfo[] = []
  let paidRows: { id: number; customer_id: number; date: string; amount_pence: number; payment_method: string | null }[]
  let invoiceRefs: InvoiceRef[] = []
  try {
    // The paid visits are a nicety: if they fail to load, the screen works as
    // before and those payments read "Not matched" (Codex review 2026-09-28).
    let refsRead: { refs: string[]; choices?: { paid_ref: string }[]; switch?: SwitchInfo[] }
    ;[owed, { customers }, refsRead, paidRows] = await Promise.all([
      api<OwedPayload>('/api/owed'),
      api<{ customers: Customer[] }>('/api/customers', 'customers'),
      api<{ refs: string[]; choices?: { paid_ref: string }[]; switch?: SwitchInfo[] }>('/api/bank-refs'),
      api<{ paid: { id: number; customer_id: number; date: string; amount_pence: number; payment_method: string | null }[] }>(`/api/bank-refs?since=${paidSince}&until=${until}`).then((r) => r.paid ?? [], () => []),
    ])
    refs = refsRead.refs
    choices = refsRead.choices ?? []
    switches = refsRead.switch ?? []
    // Several jobs on: the round's jobs, so each visit offered says which job it was.
    await loadRoundJobs()
    // Invoices still to be paid (0065), so a payment that names one's number is matched to its visits.
    if (invoicesHere()) invoiceRefs = await api<{ invoices: { number: string; customer_id: number; unpaid_clean_ids: number[] }[] }>('/api/invoices?open=1')
      .then((r) => r.invoices.map((i) => ({ number: i.number, customerId: i.customer_id, cleanIds: i.unpaid_clean_ids })), () => [])
  } catch {
    showChoose(box, "Roundly couldn't load who owes you. Check your signal and try again.")
    return
  }
  if (gen !== routeGen) return
  // Unpaid visits of any age; visits nobody recorded a payment for only from
  // shortly before the statement starts, so old history is not matched.
  const earliest = read.payments.reduce((m, p) => (p.date < m ? p.date : m), read.payments[0].date)
  const since = addDays(earliest, -45)
  // On a round with brought money a visit is matched by what is still to pay on it (credit and part payments off),
  // and what a customer owes on an account from Squeegee is matched too (release step 5).
  const moneyRound = getMe()?.broughtMoney === true
  const visits: OwedVisit[] = [
    ...owed.cleans,
    ...owed.unrecorded.cleans.filter((c) => c.date >= since),
  ]
    .filter((c) => (c.amount_pence ?? 0) > 0 && (!moneyRound || (toCollect(c) ?? 0) > 0))
    .map((c): OwedVisit => ({
      cleanId: c.id, customerId: c.customer_id, date: c.date, pence: moneyRound ? toCollect(c)! : c.amount_pence!, version: c.version, status: c.payment_status === 'unpaid' ? 'unpaid' : 'unknown', chased: c.chased,
      // Several jobs on: the visit's job, so the matching and the lists below can tell gutters from windows.
      ...(severalJobsOn() ? { jobId: visitJobId(c), job: hasExtraJobs(c.customer_id) ? visitJobLabel(c) : null } : {}),
    }))
  if (moneyRound) visits.push(...accountsAsOwed(owed.brought?.accounts ?? []))
  const people: PayerCustomer[] = customers.map((c) => ({ id: c.id, name: c.name, address: c.address, postcode: c.postcode, payer_names: c.payer_names ?? null }))
  const paidVisits: PaidVisit[] = (paidRows ?? []).map((r) => ({ cleanId: r.id, customerId: r.customer_id, date: r.date, pence: r.amount_pence, method: r.payment_method }))
  const result = matchPayments(read.payments, visits, people, new Set(refs), paidVisits, { severalJobs: severalJobsOn(), invoices: invoiceRefs })
  drawResults(box, { pdfCheck: got.pdfCheck, payments: read.payments, ignored: read.ignored, result, customers: new Map(customers.map((c) => [c.id, c])), switches, newMoney: new Set(choices.map((c) => c.paid_ref)) })
}

// "12 Aug clean", or with several jobs "12 Aug Gutters · £60" (PLAN 4: the job and the amount).
function visitWords(c: Candidate): string {
  if (!c.visits.length) return 'tick which visits'
  // Money brought from Squeegee: "22 Sep clean + £12 from Squeegee", or "£12 from Squeegee".
  const owing = c.visits.filter(isAccount)
  if (owing.length) {
    const rest = c.visits.filter((v) => !isAccount(v))
    const squeegee = `${money(owing.reduce((s, v) => s + v.pence, 0))} from Squeegee`
    return rest.length ? `${visitWords({ ...c, visits: rest })} + ${squeegee}` : squeegee
  }
  const one = (v: Candidate['visits'][number]): string => (v.job ? `${shortDate(v.date)} ${v.job} · ${money(v.pence)}` : shortDate(v.date))
  if (c.visits.some((v) => v.job)) return c.visits.length === 1 ? one(c.visits[0]) : `${c.visits.length} visits: ${c.visits.map(one).join(', ')}`
  return c.visits.length === 1 ? `${shortDate(c.visits[0].date)} clean` : `${c.visits.length} cleans: ${c.visits.map((v) => shortDate(v.date)).join(', ')}`
}

function paymentLine(m: { payment: PaymentMatch['payment'] }): HTMLElement {
  const p = m.payment
  return el('div', { class: 'bank-pay' }, el('strong', {}, `${money(p.pence)} on ${shortDate(p.date)}`), el('span', { class: 'muted' }, p.who && !p.text.includes(p.who) ? `${p.who} · ${p.text}` : p.text || 'no name on it'))
}

// "Mitchell · 12 Aug clean, marked paid by bank".
function alreadyPaidWords(m: AlreadyPaidMatch): string {
  const how = (method: string | null): string => (method === 'bank' ? 'by bank' : method === 'cash' ? 'in cash' : method ? `(${method})` : '')
  const when = m.visits.length === 1 ? `${shortDate(m.visits[0].date)} clean` : `${m.visits.length} cleans: ${m.visits.map((v) => shortDate(v.date)).join(', ')}`
  const methods = [...new Set(m.visits.map((v) => how(v.method)))].filter(Boolean)
  return `${m.name} · ${when}, marked paid${methods.length === 1 ? ` ${methods[0]}` : ''}`
}

// "Which visits was it for?" (several jobs; Astra r1, finding 3): every visit the customer owes, ticked as Roundly's
// first guess, with the total of the ticked ones against the payment. `onPick` gets the candidate with exactly the
// ticked visits when they add up to the payment, else null (nothing is marked).
function visitPicker(m: PaymentMatch, c: Candidate, onPick: (picked: Candidate | null) => void, reserved: () => Set<number>): { node: HTMLElement; current: () => Candidate | null; open: () => void; refresh: () => void } {
  const owed = c.owed ?? c.visits
  const ticked = new Set(c.visits.map((v) => v.cleanId))
  const node = el('div', { class: 'bank-picker' })
  node.append(el('p', { class: 'muted' }, c.visits.length
    ? `More than one set of ${c.name}'s visits adds up to ${money(m.payment.pence)}. Tick the visits this payment was for.`
    : `${c.name} owes more visits than Roundly can sort through. Tick the visits this ${money(m.payment.pence)} was for.`))
  const total = el('p', { class: 'bank-picker-total', role: 'status' })
  const boxes: { v: (typeof owed)[number]; box: HTMLInputElement }[] = []
  const current = (): Candidate | null => {
    const visits = owed.filter((v) => ticked.has(v.cleanId))
    const sum = visits.reduce((x, v) => x + v.pence, 0)
    return sum === m.payment.pence && visits.length ? { ...c, visits } : null
  }
  const show = (): void => {
    const sum = owed.filter((v) => ticked.has(v.cleanId)).reduce((x, v) => x + v.pence, 0)
    total.textContent = sum === m.payment.pence ? `Ticked ${money(sum)}, the same as the payment.` : `Ticked ${money(sum)}. Tick visits that add up to ${money(m.payment.pence)}.`
    total.classList.toggle('bank-picker-off', sum !== m.payment.pence)
  }
  // A visit another payment has ticked is not offered here (Astra r2, finding 8): reserved one visit at a time, so
  // the other sets stay open.
  const refresh = (): void => {
    const taken = reserved()
    for (const { v, box } of boxes) {
      box.checked = ticked.has(v.cleanId)
      box.disabled = taken.has(v.cleanId) && !ticked.has(v.cleanId)
    }
    show()
  }
  // Chosen for this payment: Roundly's first set none of whose visits another payment holds, else nothing ticked.
  const open = (): void => {
    const taken = reserved()
    const free = (set: typeof owed): boolean => set.length > 0 && set.every((v) => !taken.has(v.cleanId))
    const first = [c.visits, ...(c.alternatives ?? [])].find(free) ?? []
    ticked.clear()
    for (const v of first) ticked.add(v.cleanId)
    refresh()
  }
  for (const v of owed) {
    const box = el('input', { type: 'checkbox' }) as HTMLInputElement
    box.addEventListener('change', () => {
      if (box.checked) ticked.add(v.cleanId)
      else ticked.delete(v.cleanId)
      show()
      onPick(current())
    })
    boxes.push({ v, box })
    node.append(el('label', { class: 'bank-option bank-picker-visit' }, box, el('span', {}, isAccount(v) ? `From Squeegee · ${money(v.pence)}` : `${shortDate(v.date)}${v.job ? ` ${v.job}` : ''} · ${money(v.pence)}`)))
  }
  node.append(total)
  refresh()
  return { node, current, open, refresh }
}

function drawResults(box: HTMLElement, data: Loaded): void {
  clear(box)
  const { result } = data
  const dates = data.payments.map((p) => p.date).sort()
  const chosen = new Map<PaymentMatch, Candidate | null>()
  for (const m of result.sure) chosen.set(m, m.sure)
  for (const m of result.check) chosen.set(m, null)

  // Money brought from Squeegee (Liam Q9): a payment from before the customer's switch day may already be in what
  // they owed. It is used only once the owner says "New money"; "Already in Squeegee" leaves it out. The answer is
  // saved for that payment, so the next statement asks nothing about it again.
  const answers = new Map<PaymentMatch, 'in_squeegee' | 'new_money'>()
  const flagOf = (m: PaymentMatch): Flag | null => {
    const c = chosen.get(m)
    return c && data.switches.length ? switchFlag(m.payment, m.ref, c.customerId, data.switches, data.newMoney) : null
  }
  const usable = (m: PaymentMatch): boolean => !!chosen.get(m) && (!flagOf(m) || answers.get(m) === 'new_money')
  const flagSlots = new Map<PaymentMatch, HTMLElement>()
  const drawFlag = (m: PaymentMatch): void => {
    const slot = flagSlots.get(m)
    if (!slot) return
    clear(slot)
    const flag = flagOf(m)
    const c = chosen.get(m)
    if (!flag || !c) return
    const said = answers.get(m)
    const evidence = flag.evidence[0]
    // On the switch day itself it is "the day you moved", before it "before you moved".
    slot.append(el('p', {}, `This payment is from ${m.payment.date === flag.day ? 'the day you moved' : 'before you moved'} from Squeegee (${shortDate(flag.day)}), so it may already be in what ${c.name} owed.`))
    if (evidence) slot.append(el('p', { class: 'muted' }, `Squeegee has a ${money(evidence.pence)} payment from them on ${evidence.on_date ? shortDate(evidence.on_date) : 'an unknown day'}.`))
    const choose = (choice: 'in_squeegee' | 'new_money'): void => {
      answers.set(m, choice)
      void mutate({
        label: `Bank payment ${choice === 'new_money' ? 'is new money' : 'already in Squeegee'}: ${c.name} ${m.payment.date}`,
        method: 'POST',
        path: '/api/bank-refs/choices',
        body: { paid_ref: m.ref, customer_id: c.customerId, choice, paid_on: m.payment.date, amount_pence: m.payment.pence, ...(choice === 'in_squeegee' && evidence ? { evidence_line_id: evidence.id } : {}) },
      })
      drawFlag(m)
      refresh()
    }
    const inSqueegee = el('button', { type: 'button', class: said === 'in_squeegee' ? 'btn-primary' : 'btn-secondary', 'aria-pressed': String(said === 'in_squeegee') }, 'Already in Squeegee')
    const fresh = el('button', { type: 'button', class: said === 'new_money' ? 'btn-primary' : 'btn-secondary', 'aria-pressed': String(said === 'new_money') }, 'New money')
    inSqueegee.addEventListener('click', () => choose('in_squeegee'))
    fresh.addEventListener('click', () => choose('new_money'))
    slot.append(el('div', { class: 'bank-flag-actions' }, inSqueegee, fresh))
    if (said === 'in_squeegee') slot.append(el('p', { class: 'muted' }, 'Left out: it is not used to pay anything.'))
  }
  const flagSlot = (m: PaymentMatch): HTMLElement => {
    const slot = el('div', { class: 'bank-flag' })
    flagSlots.set(m, slot)
    return slot
  }

  const go = el('button', { type: 'button', class: 'btn-primary import-go' }) as HTMLButtonElement
  const refresh = (): void => {
    const n = [...chosen.keys()].filter(usable).length
    // A ticked payment whose switch-day question is not answered yet holds the button, as the import's money
    // questions hold Add.
    const waiting = [...chosen.keys()].some((m) => !!chosen.get(m) && !!flagOf(m) && !answers.has(m))
    go.textContent = waiting ? 'Answer the question above first' : n === 0 ? 'Nothing chosen to mark paid' : n === 1 ? 'Mark 1 payment paid by bank' : `Mark ${n} payments paid by bank`
    go.disabled = waiting || n === 0
  }

  const summary = el('section', { class: 'import-card' })
  summary.append(
    el('h3', {}, `${data.payments.length} payment${data.payments.length === 1 ? '' : 's'} in, ${shortDate(dates[0])} to ${shortDate(dates[dates.length - 1])}`),
  )
  const notes: string[] = []
  // A PDF is read from its layout: say whether the money in adds up to the
  // total the statement prints, so a misread never passes quietly.
  const pc = data.pdfCheck
  if (pc && pc.statedIn !== null) {
    notes.push(pc.statedIn === pc.readIn ? `Money in adds up to ${money(pc.readIn)}, the same as the statement.` : `Careful: Roundly read ${money(pc.readIn)} in, but the statement says ${money(pc.statedIn)}. Check each payment below against the statement.`)
  }
  if (result.alreadyUsed) notes.push(`${result.alreadyUsed} already ticked off from a statement before, so left out.`)
  notes.push(`${result.sure.length} matched, ${result.check.length} to check, ${result.alreadyPaid.length ? `${result.alreadyPaid.length} already paid, ` : ''}${result.unmatched.length} not matched.`)
  summary.append(el('ul', { class: 'import-notes' }, ...notes.map((n) => el('li', {}, n))))
  box.append(summary)

  if (result.sure.length) {
    const card = el('section', { class: 'import-card' })
    card.append(el('h3', {}, 'Matched'), el('p', { class: 'muted' }, 'One customer fits the amount and the name on the payment. Untick any that are wrong.'))
    for (const m of result.sure) {
      const c = m.sure!
      const tick = el('input', { type: 'checkbox', checked: 'checked', 'aria-label': `${c.name}, ${money(m.payment.pence)}` }) as HTMLInputElement
      tick.checked = true
      tick.addEventListener('change', () => {
        chosen.set(m, tick.checked ? c : null)
        syncTaken()
        drawFlag(m)
        refresh()
      })
      card.append(el('label', { class: 'bank-row' }, tick, el('div', {}, el('strong', {}, c.name), el('span', {}, visitWords(c)), paymentLine(m))), flagSlot(m))
      drawFlag(m)
    }
    box.append(card)
  }

  // A visit picked for one payment cannot be picked for another (review
  // finding 16): its option elsewhere is greyed out until un-picked.
  const optionRows: { m: PaymentMatch; c: Candidate; radio: HTMLInputElement; row: HTMLElement }[] = []
  // The visits other payments have chosen, one by one (several jobs pickers reserve visits, not whole guesses).
  const reservedBy = (m: PaymentMatch): Set<number> => new Set([...chosen.entries()].filter(([other, pick]) => other !== m && pick !== null).flatMap(([, pick]) => pick!.visits.map((v) => v.cleanId)))
  const pickers: { refresh: () => void }[] = []
  const syncTaken = (): void => {
    for (const o of optionRows) {
      // An option with a picker stays open: its visits are reserved one by one inside it (Astra r2, finding 8).
      const clash = !o.c.owed && [...chosen.entries()].some(([other, pick]) => other !== o.m && pick !== null && pick.visits.some((v) => o.c.visits.some((w) => w.cleanId === v.cleanId)))
      o.radio.disabled = clash && !o.radio.checked
      o.row.classList.toggle('bank-option-taken', o.radio.disabled)
    }
    for (const p of pickers) p.refresh()
  }
  if (result.check.length) {
    const card = el('section', { class: 'import-card' })
    card.append(el('h3', {}, 'Check these'), el('p', { class: 'muted' }, 'The amount fits, but Roundly is not sure who it is from. Pick who paid, or leave it.'))
    result.check.forEach((m, i) => {
      const group = el('fieldset', { class: 'bank-check' })
      group.append(el('legend', {}, paymentLine(m)))
      const options: [Candidate | null, string][] = [...m.choices.map((c): [Candidate, string] => [c, `${c.name} · ${visitWords(c)}`]), [null, 'None of these']]
      // Nobody's name or address is on the payment, only the amount fits:
      // said plainly, with the list folded away (tested on Dropby's August
      // statement: a £100 from someone not on the round offered five
      // strangers who each owed £100).
      const amountOnly = m.choices.every((c) => c.score === 0)
      let into: HTMLElement = group
      if (amountOnly) {
        group.append(el('p', { class: 'muted' }, "Nobody's name or address is on this payment. These customers only owe the same amount."))
        const fold = el('details', { class: 'bank-fold' })
        fold.append(el('summary', {}, `Show the ${m.choices.length} who owe ${money(m.payment.pence)}`))
        group.append(fold)
        into = fold
      }
      for (const [c, label] of options) {
        const radio = el('input', { type: 'radio', name: `bank-check-${i}` }) as HTMLInputElement
        radio.checked = c === null
        // Several jobs: more than one set of their visits adds up to this payment, so the owner ticks which visits
        // it was for; the payment is only used once the ticked visits add up to it exactly.
        const picker = c?.owed ? visitPicker(m, c, (picked) => {
          if (radio.checked) chosen.set(m, picked)
          syncTaken()
          refresh()
        }, () => reservedBy(m)) : null
        if (picker) pickers.push(picker)
        radio.addEventListener('change', () => {
          if (radio.checked && picker) picker.open()
          if (radio.checked) chosen.set(m, picker ? picker.current() : c)
          if (picker) picker.node.hidden = !radio.checked
          syncTaken()
          drawFlag(m)
          refresh()
        })
        const row = el('label', { class: 'bank-option' }, radio, el('span', {}, label))
        if (c) optionRows.push({ m, c, radio, row })
        ;(c ? into : group).append(row)
        if (picker) {
          picker.node.hidden = true
          ;(c ? into : group).append(picker.node)
        }
      }
      group.append(flagSlot(m))
      card.append(group)
    })
    box.append(card)
  }

  if (result.alreadyPaid.length) {
    const done = el('details', { class: 'import-card bank-unmatched bank-already-paid' })
    done.append(el('summary', {}, `Already paid (${result.alreadyPaid.length})`))
    done.append(el('p', { class: 'muted' }, 'Payments for visits you had already marked paid, with the customer\'s name or address on them. Nothing to do.'))
    for (const m of result.alreadyPaid) done.append(el('div', { class: 'bank-row' }, paymentLine(m), el('span', { class: 'bank-hint' }, alreadyPaidWords(m))))
    box.append(done)
  }

  if (result.unmatched.length) {
    const more = el('details', { class: 'import-card bank-unmatched' })
    more.append(el('summary', {}, `Not matched (${result.unmatched.length})`))
    more.append(el('p', { class: 'muted' }, "Money in that fits nobody who owes: a new customer, a different amount, or not for a clean. Nothing happens to these."))
    for (const m of result.unmatched) more.append(el('div', { class: 'bank-row' }, paymentLine(m), m.hint ? el('span', { class: 'bank-hint' }, m.hint) : ''))
    box.append(more)
  }

  const other = el('button', { type: 'button', class: 'link-button' }, 'Choose a different file')
  other.addEventListener('click', () => showChoose(box))
  const clashNote = el('p', { class: 'form-error', role: 'alert' })
  go.addEventListener('click', () => {
    // Never the same visit for two payments (Astra r2, finding 8): checked again before anything is saved.
    const ids = [...chosen.values()].filter((c): c is Candidate => c !== null).flatMap((c) => c.visits.map((v) => v.cleanId))
    if (new Set(ids).size !== ids.length) {
      clashNote.textContent = 'Two payments are ticked against the same visit. Untick one before marking them paid.'
      return
    }
    clashNote.textContent = ''
    go.disabled = true
    // Only payments that may be used: a flagged one the owner has not called new money stays out.
    void send(box, data, new Map([...chosen.entries()].filter(([m]) => usable(m))), new Set(result.check))
  })
  syncTaken()
  refresh()
  box.append(el('div', { class: 'import-actions' }, clashNote, go, other))
}

async function send(box: HTMLElement, data: Loaded, chosen: Map<PaymentMatch, Candidate | null>, picked: Set<PaymentMatch>): Promise<void> {
  const picks = [...chosen.entries()].filter((e): e is [PaymentMatch, Candidate] => e[1] !== null)
  clear(box)
  const status = el('p', { role: 'status' }, `Marking ${picks.length} paid by bank…`)
  box.append(el('section', { class: 'import-card' }, status))
  let paid = 0
  let waiting = 0
  const failed: string[] = []
  // Payer names to remember, per customer, from every confirmed payment.
  const names = new Map<number, string | null>()
  // Every payment ticked off, each with its own Undo on the done card (bug hunt 8 Oct 2026: only payments that paid an
  // account from Squeegee had one, release step 5). A payment with a receipt (money brought from Squeegee) is undone
  // by its receipt; a visit paid on a money round by its own payment Undo; any other visit goes back to exactly what
  // it was (status, chase), the same inverse a half-saved payment already uses below. A change still waiting for
  // signal has nothing landed to undo yet, so it gets none.
  type VisitBack = { cleanId: number; version: number | null; status: OwedVisit['status']; chased: number; forwardKey?: string }
  const undoable: ({ name: string; pence: number; date: string } & ({ receipt: number } | { visits: VisitBack[]; customerId: number; ref: string }))[] = []
  // On a round with brought money (0043, Astra r3 finding 8) each save carries the day the payment shows on the
  // statement, kept on the queued change, so the money counts on the day it arrived.
  const paidOn = (m: PaymentMatch): Record<string, string> => {
    if (getMe()?.broughtMoney !== true) return {}
    const day = /^\d{4}-\d{2}-\d{2}$/.test(m.payment.date) ? m.payment.date : todayISO()
    return { paid_on: day > todayISO() ? todayISO() : day }
  }
  for (const [m, c] of picks) {
    let ok = true
    let queued = false
    const saved: { v: OwedVisit; version: number; forwardKey?: string }[] = []
    // A payment for two or more visits is saved all at once (POST /api/bank-refs/apply, several jobs PLAN 4): all
    // of them paid with this payment, or none. Closing the app half way can no longer leave one paid and the rest
    // forgotten. A payment for one visit is the ordinary visit save below. One that pays an account brought from
    // Squeegee goes all at once too, with the payment's own amount, which everything it pays must add up to (0043).
    const parts = splitPaid(c.visits)
    if (c.visits.length > 1 || parts.accounts.length) {
      const ack: Ack = await mutate({
        label: `Paid by bank: ${c.name} ${parts.visits.length ? c.visits.filter((v) => !isAccount(v)).map((v) => v.date).join(', ') : 'from Squeegee'}`,
        method: 'POST',
        path: '/api/bank-refs/apply',
        body: parts.accounts.length
          ? { paid_ref: m.ref, visits: parts.visits, accounts: parts.accounts, amount_pence: m.payment.pence, ...paidOn(m) }
          : { paid_ref: m.ref, visits: parts.visits, ...paidOn(m) },
      })
      if (ack.ok || ack.status === 0 || ack.status >= 500) {
        paid++
        if (!ack.ok) waiting++
        if (ack.ok && typeof ack.receiptId === 'number') undoable.push({ name: c.name, pence: m.payment.pence, date: m.payment.date, receipt: ack.receiptId })
        // On a round with money brought from Squeegee a payment is only ever undone by its receipt.
        // All at once: each visit's version after the save is read again when Undo is tapped (below).
        else if (ack.ok && !parts.accounts.length && getMe()?.broughtMoney !== true) {
          undoable.push({ name: c.name, pence: m.payment.pence, date: m.payment.date, customerId: c.customerId, ref: m.ref,
            visits: c.visits.filter((v) => !isAccount(v)).map((v) => ({ cleanId: v.cleanId, version: null, status: v.status, chased: v.chased })) })
        }
        if (c.rememberable || picked.has(m)) {
          const cust = data.customers.get(c.customerId)
          const before = names.has(c.customerId) ? names.get(c.customerId)! : cust?.payer_names ?? null
          names.set(c.customerId, withPayerName(before, payerNameOf(m.payment)))
        }
      } else {
        for (const q of getQueue()) if ((q.status === 'conflict' || q.status === 'failed') && q.path === '/api/bank-refs/apply' && q.label.startsWith(`Paid by bank: ${c.name} `)) dismiss(q.key)
        failed.push(ack.code === 'ref_used' ? `${c.name}: this payment has already been used. Check them in Owed.`
          : ack.code === 'amounts_changed' ? `${c.name}: not marked, because what they owe changed since. Check them in Owed.`
          : ack.code === 'needs_choice' || ack.code === 'in_squeegee' ? `${c.name}: not marked, because it may already be in Squeegee's figure.`
          : `${c.name}: not marked, because a visit changed on another phone. Check them in Owed.`)
      }
      continue
    }
    for (const v of c.visits) {
      const ack: Ack<{ version?: number }> = await mutate({
        label: `Paid by bank: ${c.name} ${v.date}`,
        method: 'PATCH',
        path: `/api/cleans/${v.cleanId}`,
        body: { version: v.version, payment_status: 'paid', payment_method: 'bank', chased: 0, paid_ref: m.ref, ...paidOn(m) },
      })
      // No signal, or a server error: the change stays queued and sends
      // itself later, so it counts as done (round 2, finding 5). Only a
      // refusal (4xx) means this payment cannot be applied.
      if (ack.ok) saved.push({ v, version: ack.row?.version ?? v.version + 1, forwardKey: (ack.row as { _payment_effect?: { forward_key?: string } } | undefined)?._payment_effect?.forward_key })
      else if (ack.status === 0 || ack.status >= 500) queued = true
      else {
        ok = false
        // Our own message says what happened; the red "edited elsewhere"
        // entry would only repeat it and hold back later tries (finding 10).
        for (const q of getQueue()) if (q.status === 'conflict' && q.path === `/api/cleans/${v.cleanId}`) dismiss(q.key)
        break
      }
    }
    if (ok) {
      paid++
      if (queued) waiting++
      else if (saved.length && (getMe()?.broughtMoney !== true || saved.every((x) => x.forwardKey))) undoable.push({ name: c.name, pence: m.payment.pence, date: m.payment.date, customerId: c.customerId, ref: m.ref,
        visits: saved.map(({ v, version, forwardKey }) => ({ cleanId: v.cleanId, version, status: v.status, chased: v.chased, ...(forwardKey ? { forwardKey } : {}) })) })
      // Remembered only when the payment carried their full name, or the
      // owner picked them (review finding 10).
      if (c.rememberable || picked.has(m)) {
        const cust = data.customers.get(c.customerId)
        const before = names.has(c.customerId) ? names.get(c.customerId)! : cust?.payer_names ?? null
        names.set(c.customerId, withPayerName(before, payerNameOf(m.payment)))
      }
    } else {
      // Half a payment is never left applied (review finding 9): the visits
      // already marked go back as they were, and the payment stays free.
      for (const { v, version } of saved) {
        await mutate({ label: `Undo paid by bank: ${c.name} ${v.date}`, method: 'PATCH', path: `/api/cleans/${v.cleanId}`, body: { version, payment_status: v.status, payment_method: null, chased: v.chased } })
      }
      failed.push(queued ? `${c.name}: only part saved while the signal was out. Check them in Owed.` : `${c.name}: not marked, because a visit changed on another phone. Check them in Owed.`)
    }
  }
  for (const [id, value] of names) {
    const cust = data.customers.get(id)
    if (!cust || value === (cust.payer_names ?? null)) continue
    // Remembering the name is a help, not the job: a clash here is left alone.
    const ack = await mutate({ label: `Remember payer name: ${cust.name}`, method: 'PATCH', path: `/api/customers/${id}`, body: { version: cust.version, payer_names: value } })
    if (ack.status === 409) for (const q of getQueue()) if (q.status === 'conflict' && q.path === `/api/customers/${id}`) dismiss(q.key)
  }
  window.dispatchEvent(new CustomEvent('dropby:data-changed'))
  clear(box)
  const card = el('section', { class: 'import-card import-done' })
  card.append(el('h3', {}, paid === 1 ? '1 payment ticked off' : `${paid} payments ticked off`), el('p', {}, 'They are marked paid by bank and gone from Owed. Next time, Roundly will know these names.'))
  if (waiting) card.append(el('p', { class: 'muted' }, `${waiting} will send when your phone is back in signal.`))
  if (failed.length) card.append(el('ul', { class: 'import-notes' }, ...failed.map((t) => el('li', {}, t))))
  // Undo a tick that paid what was owed from Squeegee: the payment and all it paid come back off, and the payment is
  // free to use again.
  if (undoable.length) {
    const list = el('ul', { class: 'import-notes bank-undo-list' })
    for (const u of undoable) {
      const undo = el('button', { type: 'button', class: 'link-button' }, 'Undo')
      const line = el('li', {}, el('span', {}, `${u.name}: ${money(u.pence)} on ${shortDate(u.date)}, paid from the bank. `), undo)
      undo.addEventListener('click', () => {
        undo.setAttribute('disabled', 'disabled')
        const back = async (): Promise<Ack> => {
          if ('receipt' in u) return mutate({ label: `Undo paid by bank: ${u.name} ${u.date}`, method: 'POST', path: `/api/money-receipts/${u.receipt}/undo`, body: {} })
          let last: Ack = { ok: true, status: 200 }
          // A visit saved all at once is undone only while it is still paid by this payment, at its version now.
          let now: Map<number, { version: number; payment_status: string; paid_ref?: string | null }> | null = null
          if (u.visits.some((v) => v.version === null)) {
            try { now = new Map((await api<{ cleans: Clean[] }>(`/api/customers/${u.customerId}`)).cleans.map((cl) => [cl.id, cl as { version: number; payment_status: string; paid_ref?: string | null }])) }
            catch { return { ok: false, status: 503, error: 'no signal' } }
          }
          for (const v of u.visits) {
            const current = v.version === null ? now?.get(v.cleanId) : null
            if (v.version === null && (!current || current.payment_status !== 'paid' || current.paid_ref !== u.ref)) { last = { ok: false, status: 409 }; break }
            const version = v.version ?? current!.version
            last = v.forwardKey
              ? await mutate({ label: `Undo paid by bank: ${u.name}`, method: 'POST', path: `/api/cleans/${v.cleanId}/payment-undo`, body: { forward_key: v.forwardKey } })
              : await mutate({ label: `Undo paid by bank: ${u.name}`, method: 'PATCH', path: `/api/cleans/${v.cleanId}`, body: { version, payment_status: v.status, payment_method: null, chased: v.chased } })
            if (!last.ok && last.status !== 0) break
          }
          return last
        }
        void back().then((ack) => {
          line.replaceChildren(el('span', {}, ack.ok || ack.status === 0 ? `${u.name}: ${money(u.pence)} put back as owed.` : `${u.name}: could not undo. Check them in Owed.`))
          window.dispatchEvent(new CustomEvent('dropby:data-changed'))
        })
      })
      list.append(line)
    }
    card.append(list)
  }
  card.append(el('div', { class: 'import-actions' }, el('a', { href: '#/owed', class: 'btn-primary import-next' }, 'Back to Owed')))
  box.append(card)
}
